<template>
  <div class="recipe-ingredients">
    <!-- original vs personalized - TODO: uncomment to enable original vs personalized data switching, fully works, hidden behind measurementSystemSelector drop down in CSS so commented out for MVP -->
    <!--    <div v-if="personalizedSelector && isScaleReady" class="unit-selector">-->
    <!--      <div class="form__group">-->
    <!--        <div class="select-style">-->
    <!--          <form>-->
    <!--            <select-->
    <!--              @change="updateRecipeScale($event.target.value)"-->
    <!--              aria-label="View original or personalized recipe"-->
    <!--            >-->
    <!--              <option value="scaled" :selected="mode === 'scaled'">Personalized</option>-->
    <!--              <option value="original" :selected="mode === 'original'">Original</option>-->
    <!--            </select>-->
    <!--          </form>-->
    <!--        </div>-->
    <!--      </div>-->
    <!--    </div>-->

    <!-- measurement system form -->
    <div v-if="measurementSystemSelector" class="unit-selector">
      <div class="form__group">
        <div class="select-style">
          <form>
            <select
              v-model="formData"
              @change="updateUserProfile($event.target.value)"
              aria-label="Select Measurement System for Ingredient list below"
            >
              <option value="imperial">US Customary</option>
              <option value="metric">Metric</option>
            </select>
          </form>
        </div>
      </div>
    </div>

    <!-- ingredient list -->
    <ul class="ingredient-list">
      <li v-for="ingredient in scaledIngredients" :key="ingredient.id" class="ingredient-info">
        <span v-if="ingredient.whole_qty" class="amount">
          {{ ingredient.whole_qty }} {{ ingredient.whole_unit_name }}
        </span>
        <span v-else class="amount">
          <span v-show="user.measurementSystem === 'imperial' || !user.measurementSystem">
            {{ formatImperialQty(ingredient.imperial_qty) }}
            {{
              ingredient.imperial_qty !== '0.00' && ingredient.imperial_taxonomy
                ? ingredient.imperial_taxonomy.name
                : ''
            }}
          </span>
          <span v-show="user.measurementSystem === 'metric'">
            {{ formatMetricQty(ingredient.metric_qty) }}
            {{
              ingredient.metric_qty !== '0.00' && ingredient.metric_taxonomy
                ? ingredient.metric_taxonomy.name
                : ''
            }}
          </span>
        </span>
        <span class="ingredient">{{ ingredient.name }}</span>
        <span class="ingredient-notes">{{ ingredient.note }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapActions, mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useArticlesStore } from '@/stores/articles'
import { useEntityContextStore } from '@/stores/entity-context'
import { fbApi } from '@/utils/http-api'

/**
 * Ingredient list, but sets imperial vs metric based on user profile field.
 */
export default defineComponent({
  name: 'IngredientList',
  components: {},
  props: {
    ingredients: {
      type: Array,
      required: true,
    },
    measurementSystemSelector: {
      type: Boolean,
      default: false,
    },
    personalizedSelector: {
      type: Boolean,
      default: false,
    },
  },

  /* state */
  data() {
    return {
      formData: '',
      loading: false,
    }
  },

  /* computed */
  computed: {
    ...mapState(useEntityContextStore, ['context']),
    ...mapState(useUserAuthStore, ['user', 'features', 'isFbAdminUser', 'settings']),
    ...mapState(useArticlesStore, ['mode', 'scaleLoading', 'scaleError']),

    // proxy to getter so that on changes to pinia ingredients
    // scaledIngredients will also update when mode changes
    scaledIngredients() {
      return this.getScaledIngredients(this.ingredients)
    },

    isPlanContext() {
      if (
        this.context &&
        this.context.context === 'plan' &&
        this.context.planDayId &&
        this.context.planDayScheduleId
      ) {
        return true
      }

      return false
    },

    hasEnergySettings() {
      return !!(this.settings && this.settings.energy && this.settings.energy.dailyCalorieType)
    },

    isScaleReady() {
      return (
        !this.scaleError &&
        !this.scaleLoading &&
        this.isPlanContext &&
        this.hasEnergySettings &&
        this.hasEnergySettings
      )
    },
  },

  /* lifecycle */
  created() {
    // update default state from store to power form
    this.formData = this.user.measurementSystem || 'imperial'
  },

  // watch: {
  //   'user.measurementSystem'
  // }

  /* component methods */
  methods: {
    ...mapActions(useUserAuthStore, ['updateUser']),
    ...mapActions(useArticlesStore, ['scaleRecipe', 'resetRecipe', 'getScaledIngredients']),

    // for imperial, change decimals to fractions
    formatImperialQty(qty) {
      qty = `${qty}` // force string

      if (qty === '0.00') {
        return '--'
      }

      return (
        qty
          // remove any .00 to show whole number
          .replace('.00', '')

          // 1/2
          .replace('.50', ' ½')

          // 1/3, 2/3
          .replace('.33', ' ⅓')
          .replace('.67', ' ⅔')

          // 1/4, 3/4
          .replace('.25', ' ¼')
          .replace('.75', ' ¾')

          // replace any 0 with a space (fractions), but have to have a space
          .replace('0 ', '')
      )
    },

    // for metric, keep decimals and only remove .00
    formatMetricQty(qty) {
      qty = `${qty}` // force string

      if (qty === '0.00' || Number.parseFloat(qty) <= 0) {
        return '--'
      }

      return (
        qty
          // remove any .00 to show whole number
          .replace('.00', '')

          // remove any ending 0, EX: 1.90 -> 1.9
          .replace(/\.([0-9])0$/, '.$1')
      )
    },

    // view orignial vs scaled recipe
    updateRecipeScale(selectedOption) {
      if (selectedOption === 'scaled') {
        this.scaleRecipe()
        return
      }

      // original
      this.resetRecipe()
    },

    // update users preference
    async updateUserProfile(measurementSystem) {
      // instantly update pinia so UI is fast, but XHR to backend for next page load
      this.updateUser({
        measurementSystem,
      })

      // if not logged in, stop
      // if fb admin (preview) do not call XHR
      if (!this.user.isLoggedIn || this.isFbAdminUser) {
        return
      }

      try {
        // update users profile
        await fbApi.put('/my/account', {
          json: {
            measurement_system: measurementSystem,
          },
        })
      } catch (e) {
        // noop, not rolling back the user setting, but it is not saved either
      }
    },
  },
})
</script>
